import { useMemo } from 'react';
import {
  computeCoverageMatrix,
  coverageCells,
  coverageFieldChoices,
  coverageHeaderRuns,
  metricFn,
  METRIC_FULL_FORMAT,
  reconcileCoverageLevels,
  type CoverageLevel,
  type MetricKey,
  type Perspective,
  type SeparationState,
  offeredMetrics,
} from '@integrate/core';
import { MetricToggle } from './MetricToggle.js';
import { ICON, IconButton } from '../ui/Icon.js';

/*
  Where each as-is group's people end up.

  A row is an as-is group, a column is the group it lands in on the chosen side, and a cell is the share
  of the row that got there. So the diagonal is continuity — people staying where they were — and an
  off-diagonal is a move. A weak diagonal or a strong off-diagonal is what a reader is here to find, so
  both are flagged.

  EVERY DECISION ON THIS PAGE IS IN CORE. Which cells print a percentage, which print an arrival, which
  are flagged, how dark the shading is, and where a header group starts and stops — all of it was inline
  in the app's renderer, and all of it is `coverageCells` and `coverageHeaderRuns` now. That is the whole
  reason two of its defects survived: a header run identified by a space-joined prefix merged "Ops ›
  Support Desk" with "Ops Support › Desk" into one column labelled neither, and a cell could not say
  whether a group had no as-is base or an as-is base weighing nothing.

  UNFILTERED, and it says so. The app's version has its own filter set, which is a second lens on a page
  whose numbers are already a comparison; this prices the whole design, as the stranded pane does.
*/

const PERSPECTIVES: { key: Extract<Perspective, 'remain' | 'carve-out'>; label: string }[] = [
  { key: 'carve-out', label: 'Carve-out' },
  { key: 'remain', label: 'RemainCo' },
];

export function CoverageMatrixPane({
  state,
  metric,
  onMetric,
  levels,
  onLevels,
  perspective,
  onPerspective,
}: {
  state: SeparationState;
  /** Shared with the phase bridge and Dedication: all three mean raw salary by "Cost". */
  metric: MetricKey;
  onMetric(next: MetricKey): void;
  levels: CoverageLevel[];
  onLevels(next: CoverageLevel[]): void;
  perspective: 'remain' | 'carve-out';
  onPerspective(next: 'remain' | 'carve-out'): void;
}): JSX.Element {
  const choices = useMemo(() => coverageFieldChoices(state), [state]);

  /*
    Reconciled on every render, not seeded once.

    The app's level list is a module global written at first open and overwritten by a deep link, never
    revalidated — so on a project with no Legal Entity column its entity shortcut set a field that does
    not exist, no option matched, the control displayed "Division", and the matrix collapsed to one cell
    reading 100% labelled "(No Legal Entity)". Opening a second project was the same failure by another
    route, with the first project's level labels left behind.
  */
  const { levels: active, dropped } = useMemo(
    () => reconcileCoverageLevels(state, levels),
    [state, levels],
  );

  const matrix = useMemo(
    () => computeCoverageMatrix(state, { levels: active, perspective, fn: metricFn(metric, state.assumptions) }),
    [state, active, perspective, metric],
  );
  const cells = useMemo(() => coverageCells(matrix), [matrix]);
  const runs = useMemo(() => coverageHeaderRuns(matrix, active.length), [matrix, active.length]);
  const format = METRIC_FULL_FORMAT[metric];

  const setLevel = (index: number, fieldId: string): void => {
    const field = choices.find((f) => f.id === fieldId);
    if (!field) return;
    const next = [...active];
    next[index] = { fieldId: field.id, label: field.label };
    onLevels(next);
  };

  return (
    <div data-testid="coverage-pane" data-levels={active.length} data-perspective={perspective}>
      <div className="row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="muted small">As-is →</span>
        <div className="tabs" role="group" aria-label="Destination">
          {PERSPECTIVES.map((p) => (
            <button
              key={p.key}
              className={`toggle-btn${perspective === p.key ? ' active' : ''}`}
              aria-pressed={perspective === p.key}
              data-testid={`coverage-side-${p.key}`}
              onClick={() => onPerspective(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <MetricToggle
          value={metric}
          onChange={onMetric}
          keys={offeredMetrics(state, ['hc', 'fte', 'cost'])}
          weight="raw"
          testIdPrefix="coverage-metric"
        />
      </div>

      <div className="row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted small">Grouped by</span>
        {active.map((level, i) => (
          <span key={`${level.fieldId}-${i}`} className="row" style={{ gap: 4, alignItems: 'center' }}>
            <select
              data-testid={`coverage-level-${i}`}
              value={level.fieldId}
              onChange={(e) => setLevel(i, e.target.value)}
            >
              {choices.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            {active.length > 1 && (
              <IconButton
                icon={ICON.remove}
                testId={`coverage-level-remove-${i}`}
                label={`Remove ${level.label}`}
                onClick={() => onLevels(active.filter((_, j) => j !== i))}
              />
            )}
          </span>
        ))}
        {choices.length > active.length && (
          <button
            data-testid="coverage-level-add"
            onClick={() => {
              const next = choices.find((f) => !active.some((l) => l.fieldId === f.id));
              if (next) onLevels([...active, { fieldId: next.id, label: next.label }]);
            }}
          >
            + level
          </button>
        )}
      </div>

      {/*
        Said, not swallowed. A level silently disappearing is how the app's version ended up describing a
        hierarchy the control was not showing.
      */}
      {dropped.length > 0 && (
        <p className="muted small" data-testid="coverage-dropped">
          {dropped.join(', ')} {dropped.length === 1 ? 'is' : 'are'} no longer a field on this project, so{' '}
          {dropped.length === 1 ? 'it has' : 'they have'} been dropped from the breakdown.
        </p>
      )}

      <p className="muted small">
        A row is an as-is group and a column is where its people land in{' '}
        <strong>{perspective === 'remain' ? 'RemainCo' : 'the carve-out'}</strong>. Each percentage is
        that row&apos;s share, measured in {metric === 'hc' ? 'headcount' : metric === 'fte' ? 'FTE' : 'raw salary'} —
        so a row sums to the share of the as-is group that reached this side, not to 100%. The diagonal is
        continuity. Unfiltered: it describes the whole design, whatever the chart behind it is narrowed to.
      </p>

      {matrix.leaves.length === 0 ? (
        <p className="muted" data-testid="coverage-empty">
          Nobody is assigned to this side yet, so there is nothing to trace.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data-table cov-table" data-testid="coverage-matrix">
            <thead>
              {runs.map((row, level) => (
                <tr key={level}>
                  {level === 0 && (
                    <th rowSpan={runs.length} className="cov-corner">
                      As-is → {perspective === 'remain' ? 'RemainCo' : 'Carve-out'}
                    </th>
                  )}
                  {row.map((run) => (
                    <th
                      key={`${run.start}-${run.label}`}
                      colSpan={run.span}
                      data-testid={`coverage-colhead-${level}-${run.start}`}
                      data-span={run.span}
                      title={run.label}
                    >
                      {run.label}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {matrix.leaves.map((leaf, r) => (
                <tr key={leaf} data-testid={`coverage-row-${r}`} data-key={leaf}>
                  <th scope="row" title={leaf}>
                    {(matrix.segments[leaf] ?? [leaf]).map((seg, i) => (
                      <span key={i} className={i < active.length - 1 ? 'muted small' : undefined}>
                        {i > 0 ? ' › ' : ''}
                        {seg}
                      </span>
                    ))}
                  </th>
                  {cells[r]!.map((cell, c) => (
                    <Cell key={c} cell={cell} format={format} index={c} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small" data-testid="coverage-legend">
        A cell is shaded in proportion to its percentage. <strong>Flagged</strong> cells are the ones
        worth looking at: a diagonal at or below 10% (the group has largely gone elsewhere) or at or above
        80% (it moved across intact), and any off-diagonal at or above 80%. <strong>+n</strong> is an
        arrival with no as-is population to be a share of — a group that did not exist before.{' '}
        <strong>n, unweighted</strong> is a group that DOES have as-is people whose{' '}
        {metric === 'hc' ? 'headcount' : metric === 'fte' ? 'FTE' : 'salary'} is zero, so there is no
        denominator; that is a gap in the data, not a new group, and the two used to look identical.
      </p>
    </div>
  );
}

function Cell({
  cell,
  format,
  index,
}: {
  cell: ReturnType<typeof coverageCells>[number][number];
  format: (v: number) => string;
  index: number;
}): JSX.Element {
  const classes = ['num'];
  if (cell.diagonal) classes.push('cov-diag');
  if (cell.flagged) classes.push('cov-flag');
  if (cell.kind === 'new') classes.push('cov-new');
  if (cell.kind === 'no-weight') classes.push('cov-noweight');

  return (
    <td
      className={classes.join(' ')}
      data-testid={`coverage-cell-${index}`}
      data-kind={cell.kind}
      data-num={cell.num}
      data-denom={cell.denom}
      data-pct={cell.pct ?? ''}
      data-flagged={cell.flagged ? 'true' : undefined}
      /*
        The shading is a background alpha rather than a colour step: the cell's own text has to stay
        readable at every intensity, and a ramp of solid fills would need a matching text ramp.
      */
      style={cell.intensity > 0 ? { background: `rgba(0, 51, 141, ${cell.intensity.toFixed(3)})` } : undefined}
      title={
        cell.pct === null
          ? `${format(cell.num)} arriving, with no as-is base to compare against`
          : `${format(cell.num)} of ${format(cell.denom)}`
      }
    >
      {cell.kind === 'pct' && `${Math.round(cell.pct!)}%`}
      {cell.kind === 'new' && `+${format(cell.num)}`}
      {cell.kind === 'no-weight' && (
        <span title="This group has as-is people, but they weigh nothing under this metric">
          {format(cell.num)}
          <span className="muted small"> unweighted</span>
        </span>
      )}
    </td>
  );
}
